import { FC, useEffect, useMemo } from 'react'
import { LAYER_METAS } from 'constants/future'
import { FutureLayerUID, FutureLayer } from 'components/features/future/types'
import { FuturePanePreset } from 'components/features/future/FuturePanePreset/FuturePanePreset'
import * as yup from 'yup'
import { useLayerSelector, extendCommonSchema } from 'components/features/future/helpers'
import { useFormContext } from 'react-hook-form'
import { FutureWhenInput } from 'components/features/future/FutureWhenInput/FutureWhenInput'
import { FutureFieldSlider } from 'components/features/future/FutureField/FutureFieldSlider'
import { schemaNumber } from 'schemas/baseSchema'
import { maskCurrencyFromZero } from 'components/atoms/inputs/maskedInput'
import { SellAssetFields } from 'components/features/future/SellAssetFields/SellAssetFields'
import { schemaAssetSell } from 'schemas/futureSchema'
import { ObjectSchema } from 'yup'
import { useMutableRef } from 'hooks/useMutableRef'
import { useCollectLayerErrors } from 'components/features/future/hooks/useCollectErrors'
import { LayerStatus } from 'components/features/future/LayerStatus/LayerStatus'

const LAYER_SLUG = 'sell_home' as const
type LayerSlugType = typeof LAYER_SLUG
type LayerType = Extract<FutureLayer, { slug: LayerSlugType }>

const schemaCreator = extendCommonSchema<LayerSlugType>(
  yup
    .object()
    .concat(yup.object({
      sales_price: schemaNumber,
      fees: schemaNumber,
    }))
    .concat(schemaAssetSell) as ObjectSchema<LayerType>
)

type FutureLayerPaneSellHomeProps = {
  uid?: FutureLayerUID
  onCancel: () => void
}

export const FutureLayerPaneSellHome: FC<FutureLayerPaneSellHomeProps> = (props) => {
  const { onCancel, uid } = props
  const layer = useLayerSelector<LayerSlugType>(props)

  const { icon, name, type } = LAYER_METAS[LAYER_SLUG]

  return (
    <FuturePanePreset<LayerSlugType>
      layer={ layer }
      slug={ LAYER_SLUG }
      name={ name }
      icon={ icon }
      type={ type }
      schemaCreator={ schemaCreator }
      onCancel={ onCancel }
    >
      <SellHomeFields uid={ uid } />
    </FuturePanePreset>
  )
}

type FutureLayerPaneSellHomeFormProps = {
  uid?: FutureLayerUID
}
const SellHomeFields: FC<FutureLayerPaneSellHomeFormProps> = ({ uid }) => {
  const { watch, setValue, getValues } = useFormContext<LayerType>()
  const setValueRef = useMutableRef(setValue)
  const sales_price = watch('sales_price')

  const errors = useCollectLayerErrors<LayerType>()
  const sellFieldsChild = useMemo(() => (
    <SellAssetFields
      errors={ errors }
      uid={ uid }
      type='home'
    />
  ), [errors, uid])

  useEffect(() => {
    const currentFees = getValues('fees')
    if (currentFees !== undefined) {
      return
    }

    const max = Math.max(sales_price || 0, 0)
    setValueRef.current('fees', Math.round(max * 0.05))
  }, [getValues, sales_price, setValueRef])

  const feesRange = useMemo(() => {
    const max = Math.max(sales_price || 0, 0)
    return {
      max,
      step: max > 0 ? max / 20 : 1,
    }
  }, [sales_price])

  const completeChild = useMemo(() => (
    <>
      <h1>
        Event Completed
      </h1>
      Don’t forget to update your Settings:
      <ul>
        <li>Unlink your old home to remove it from your Assets</li>
        <li>Unlink your old mortgage to remove it from your Debts</li>
        <li>Update your Housing Type (if it has changed)</li>
      </ul>
    </>
  ), [])

  return (
    <LayerStatus uid={ uid || null } completeContent={ completeChild } skipPartialState type='event'>
      <FutureWhenInput error={ errors.whenField } />

      { sellFieldsChild }

      <FutureFieldSlider
        label='Fees'
        name='fees'
        mask={ maskCurrencyFromZero }

        note='Sellers usually pay 5% the sale price in fees.'
        error={ errors.fees }

        min={ 0 }
        max={ feesRange.max }
        step={ feesRange.step }
      />
    </LayerStatus>
  )
}
